/**
 * Shared helper for comparing search filters while ignoring shape/pagination keys.
 *
 * Used by:
 *  - searchResults reducer  → decides whether to clear retainedSearchResults
 *  - RapidSearchResultsWithHandlerV2 → decides whether to clear property selection
 */

/**
 * Keys that represent pagination or UI-internal state and must be ignored
 * when comparing whether the user changed a "real" search filter.
 */
const SHAPE_PAGINATION_KEYS = new Set([
    'mapSearch', 'offset', 'sourceOffsets', 'propertyId',
]);

/**
 * Returns a shallow copy of `filters` with all shape/pagination keys removed.
 *
 * @param {Object} filters - search filters object from Redux state
 * @returns {Object} filters with shape/pagination keys stripped
 */
export const getNonShapeFilters = (filters) =>
    Object.fromEntries(
        Object.entries(filters || {}).filter(([k]) => !SHAPE_PAGINATION_KEYS.has(k)),
    );

/**
 * Returns true when the user changed a base search filter (beds, baths,
 * price, sort …) between two filter snapshots while map shapes were active.
 *
 * When this happens the retained data was fetched under the OLD filter criteria
 * and must not be merged with results from the new filter set.
 *
 * @param {Object} prevFilters - previous searchFilters from Redux
 * @param {Object} newFilters  - current searchFilters from Redux
 * @returns {boolean}
 */
export const haveNonShapeFiltersChanged = (prevFilters, newFilters) =>
    JSON.stringify(getNonShapeFilters(prevFilters)) !==
    JSON.stringify(getNonShapeFilters(newFilters));

